import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  DollarSign, Users, TrendingUp, Activity, ArrowRight, Loader2,
} from "lucide-react";
import { fetchAdminEngagement } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminTopline — live "what matters right now" hero strip on /admin.
 *
 * Two metrics matter at this stage of the company:
 *   1. Revenue — anything moves here, founder needs to know immediately.
 *   2. User acquisition — sign-ups + MAU + activation rate.
 *
 * This is shown above MembersPanel (which is the WHO) and the nav
 * cards (which are the WHERE TO GO). It's the answer to "what
 * happened since I last looked?" without needing to click anywhere.
 *
 * Data flow
 * ─────────
 * Fetches /api/admin/revenue + /api/admin/engagement in parallel on
 * mount. We don't poll — page revisit is the refresh action (it's
 * Iyal's home, she's coming back every few hours anyway).
 *
 * Layout
 * ──────
 * Four tiles on desktop, two columns on tablet, stack on mobile. Each
 * tile is a Link that jumps straight to the deeper page for that
 * metric — so the topline is also navigation.
 */

const fmtMoney = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat(undefined, {
        style: "currency", currency: "USD",
        minimumFractionDigits: n < 100 ? 2 : 0,
        maximumFractionDigits: n < 100 ? 2 : 0,
      }).format(n)
    : "—";
const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString() : "—";
const fmtPct = (n) =>
  typeof n === "number" ? `${n.toFixed(1)}%` : "—";

const Tile = ({
  to, eyebrow, value, label, delta, icon: Icon, accent, testid,
}) => (
  <Link
    to={to}
    className={`group block border rounded-2xl p-5 transition-all ${
      accent === "money"
        ? "bg-gradient-to-br from-[#D4B483]/15 via-[#D4B483]/5 to-transparent border-[#D4B483]/30 hover:border-[#D4B483]/60"
        : accent === "growth"
        ? "bg-gradient-to-br from-[#7BAE8E]/15 via-[#7BAE8E]/5 to-transparent border-[#7BAE8E]/30 hover:border-[#7BAE8E]/60"
        : accent === "engagement"
        ? "bg-gradient-to-br from-[#F4B8C0]/12 via-[#F4B8C0]/4 to-transparent border-[#F4B8C0]/30 hover:border-[#F4B8C0]/60"
        : "bg-white/[0.04] border-white/10 hover:border-white/30"
    }`}
    data-testid={testid}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon
          className={`h-4 w-4 ${
            accent === "money"
              ? "text-[#D4B483]"
              : accent === "growth"
              ? "text-[#A5D3B5]"
              : accent === "engagement"
              ? "text-[#F4B8C0]"
              : "text-white/60"
          }`}
        />
        <span
          className={`text-[10px] uppercase tracking-[0.22em] ${
            accent === "money"
              ? "text-[#D4B483]"
              : accent === "growth"
              ? "text-[#A5D3B5]"
              : accent === "engagement"
              ? "text-[#F4B8C0]"
              : "text-white/55"
          }`}
        >
          {eyebrow}
        </span>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-white/30 group-hover:text-white/70 group-hover:translate-x-0.5 transition-all" />
    </div>
    <div
      className="text-3xl sm:text-4xl font-light text-white tabular-nums mb-1 leading-none"
      data-testid={`${testid}-value`}
    >
      {value}
    </div>
    <div className="text-sm text-white/65 leading-snug">{label}</div>
    {delta !== null && delta !== undefined && (
      <div className="text-xs text-white/45 mt-2 tabular-nums">{delta}</div>
    )}
  </Link>
);

const SkeletonTile = () => (
  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 animate-pulse">
    <div className="h-3 w-20 bg-white/10 rounded mb-4" />
    <div className="h-8 w-24 bg-white/10 rounded mb-2" />
    <div className="h-3 w-32 bg-white/10 rounded" />
  </div>
);

const AdminTopline = () => {
  const [revenue, setRevenue] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rev, eng] = await Promise.all([
          axios.get(`${API}/admin/revenue`, { withCredentials: true })
            .then((r) => r.data),
          fetchAdminEngagement(),
        ]);
        if (cancelled) return;
        setRevenue(rev);
        setEngagement(eng);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status || "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div
        className="bg-[#F4B8C0]/10 border border-[#F4B8C0]/30 rounded-2xl p-5 text-sm text-[#F4B8C0] mb-8"
        data-testid="admin-topline-error"
      >
        Couldn't load topline metrics (status {error}). The deeper
        admin pages may still work — pick a card below.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[0, 1, 2, 3].map((i) => <SkeletonTile key={i} />)}
      </div>
    );
  }

  // Derive what we need
  const mrr = revenue?.mrr_estimate ?? 0;
  const last30Rev = revenue?.last_30d?.revenue ?? 0;
  const activePro = revenue?.active_pro_subscribers ?? 0;
  const lifetimeRev = revenue?.lifetime?.revenue ?? 0;
  const lifetimePaid = revenue?.lifetime?.paid_count ?? 0;

  const totalMembers = engagement?.composition?.total_members ?? 0;
  const mau = engagement?.active_users?.mau ?? 0;
  const wau = engagement?.active_users?.wau ?? 0;
  const dau = engagement?.active_users?.dau ?? 0;
  const signupToPro =
    engagement?.conversion_funnel?.conversion_pct?.signup_to_pro;
  const signupToRoutine =
    engagement?.conversion_funnel?.conversion_pct?.signup_to_first_routine;

  return (
    <div data-testid="admin-topline">
      {/* The 4 numbers that matter most right now */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Tile
          to="/admin/revenue"
          eyebrow="Lifetime revenue"
          value={fmtMoney(lifetimeRev)}
          label={
            lifetimePaid === 0
              ? "Waiting for your first sale 🤎"
              : `${fmt(lifetimePaid)} paid transaction${lifetimePaid === 1 ? "" : "s"}`
          }
          delta={
            last30Rev > 0
              ? `${fmtMoney(last30Rev)} in last 30 days`
              : last30Rev === 0 && lifetimePaid > 0
              ? "0 in last 30 days"
              : null
          }
          icon={DollarSign}
          accent="money"
          testid="topline-revenue"
        />
        <Tile
          to="/admin/revenue"
          eyebrow="Active Pro · MRR"
          value={fmt(activePro)}
          label={`${fmtMoney(mrr)} estimated MRR`}
          delta={
            activePro === 0
              ? "First Pro = lifetime founding member"
              : null
          }
          icon={TrendingUp}
          accent="growth"
          testid="topline-active-pro"
        />
        <Tile
          to="/admin/engagement"
          eyebrow="Members · MAU"
          value={fmt(totalMembers)}
          label={`${fmt(mau)} active in last 30d`}
          delta={
            totalMembers > 0
              ? `DAU ${fmt(dau)} · WAU ${fmt(wau)}`
              : null
          }
          icon={Users}
          accent="engagement"
          testid="topline-members"
        />
        <Tile
          to="/admin/engagement"
          eyebrow="Activation · conversion"
          value={fmtPct(signupToRoutine)}
          label="Signup → first routine"
          delta={
            signupToPro !== null && signupToPro !== undefined
              ? `${fmtPct(signupToPro)} → Pro end-to-end`
              : null
          }
          icon={Activity}
          accent="engagement"
          testid="topline-conversion"
        />
      </div>
    </div>
  );
};

export default AdminTopline;
